import { BigNumber } from 'ethers';
import { useEffect, useState } from 'react';
import {
  TectonicAsset,
  PartnerTokenRate,
} from '@cronos-labs/tectonic-sdk/dist/types';

import useAppDispatch from '@hooks/useAppDispatch';
import useTectonicAssetPricer from '@hooks/useTectonicAssetPricer';
import { useTectonicSdk } from '@providers/TectonicSdkProvider';
import { getAssetBorrowAmount, getAssetSupplyAmount } from '@redux/markets';
import {
  getAssetBorrowAmount as getDefiAssetBorrowAmount,
  getAssetSupplyAmount as getDefiAssetSupplyAmount,
} from '@redux/defiMarkets';
import {
  getAssetBorrowAmount as getLcroAssetBorrowAmount,
  getAssetSupplyAmount as getLcroAssetSupplyAmount,
} from '@redux/venoMarkets';
import { getAssetUsdPrice } from '@redux/usdPrices';
import { notify } from '@lib/bugsnag';

import { Mode } from './types';

interface MarketsPageData {
  borrowApy: Record<string, number>;
  netBorrowApy: Record<string, number>;
  borrowDailyDistributionAmount: Record<string, Undefined<BigNumber>>;
  supplyApy: Record<string, number>;
  netSupplyApy: Record<string, number>;
  supplyDailyDistributionAmount: Record<string, Undefined<BigNumber>>;
}

export interface MarketsPageIsolatedPoolData extends MarketsPageData {
  borrowDailyDistributionAmountOfPartnerToken: Record<
    string,
    Undefined<PartnerTokenRate[]>
  >;
  supplyDailyDistributionAmountOfPartnerToken: Record<
    string,
    Undefined<PartnerTokenRate[]>
  >;
}

export interface MarketsPageDataResult {
  data: MarketsPageIsolatedPoolData;
  updateNetBorrowApy: (symbol: string, netApy: number) => void;
  updateNetSupplyApy: (symbol: string, netApy: number) => void;
}

function useMarketsPageData(
  supportedAssets: TectonicAsset[],
  mode: Mode
): MarketsPageDataResult {
  const dispatch = useAppDispatch();
  const sdk = useTectonicSdk(mode);
  const tectonicAssetPricer = useTectonicAssetPricer(sdk);
  const [data, setData] = useState<MarketsPageIsolatedPoolData>({
    borrowApy: {},
    borrowDailyDistributionAmount: {},
    borrowDailyDistributionAmountOfPartnerToken: {},
    supplyApy: {},
    supplyDailyDistributionAmount: {},
    supplyDailyDistributionAmountOfPartnerToken: {},
    netBorrowApy: {},
    netSupplyApy: {},
  });

  useEffect(() => {
    function getAssetsBorrowAmount(): void {
      supportedAssets.forEach((asset) => {
        switch (mode) {
          case 'MAIN':
            dispatch(getAssetBorrowAmount(sdk, asset));
            break;
          case 'DEFI':
            dispatch(getDefiAssetBorrowAmount(sdk, asset));
            break;
          case 'VENO':
            dispatch(getLcroAssetBorrowAmount(sdk, asset));
            break;
          default:
            break;
        }
      });
    }

    function getAssetsSupplyAmount(): void {
      supportedAssets.forEach((asset) => {
        switch (mode) {
          case 'MAIN':
            dispatch(getAssetSupplyAmount(sdk, asset));
            break;
          case 'DEFI':
            dispatch(getDefiAssetSupplyAmount(sdk, asset));
            break;
          case 'VENO':
            dispatch(getLcroAssetSupplyAmount(sdk, asset));
            break;
          default:
            break;
        }
      });
    }

    async function getAssetSupplyApy(asset: TectonicAsset): Promise<void> {
      const [
        assetSupplyApy,
        assetSupplyDailyDistributionAmount,
        nonTonicSupplyDailyDistributeAmount,
      ] = await Promise.all([
        sdk.Supply.supplyApy(asset.tTokenAddress),
        sdk.Tonic.tonicDailyDistributeRateForSupply(asset.tTokenAddress),
        ...(mode === 'MAIN' || mode === 'VENO'
          ? []
          : [
              sdk.Tonic.nonTonicDailyDistributeRateForSupply(
                asset.tTokenAddress
              ),
            ]),
      ]);

      setData(
        ({
          supplyApy,
          supplyDailyDistributionAmount,
          supplyDailyDistributionAmountOfPartnerToken,
          ...rest
        }) => ({
          ...rest,
          supplyApy: { ...supplyApy, [asset.symbol]: assetSupplyApy },
          supplyDailyDistributionAmount: {
            ...supplyDailyDistributionAmount,
            [asset.symbol]: assetSupplyDailyDistributionAmount,
          },
          supplyDailyDistributionAmountOfPartnerToken: {
            ...supplyDailyDistributionAmountOfPartnerToken,
            [asset.symbol]: nonTonicSupplyDailyDistributeAmount,
          },
        })
      );
    }

    async function getAssetsSupplyApy(): Promise<void> {
      try {
        await Promise.all(
          supportedAssets.map((asset) => getAssetSupplyApy(asset))
        );
      } catch (error) {
        notify(error);
      }
    }

    async function getAssetBorrowApy(asset: TectonicAsset): Promise<void> {
      const [
        assetBorrowApy,
        assetBorrowDailyDistributionAmount,
        nonTonicBorrowDailyDistributeAmount,
      ] = await Promise.all([
        sdk.Borrow.borrowApy(asset.tTokenAddress),
        sdk.Tonic.tonicDailyDistributeRateForBorrow(asset.tTokenAddress),
        ...(mode === 'MAIN' || mode === 'VENO'
          ? []
          : [
              sdk.Tonic.nonTonicDailyDistributeRateForBorrow(
                asset.tTokenAddress
              ),
            ]),
      ]);

      setData(
        ({
          borrowApy,
          borrowDailyDistributionAmount,
          borrowDailyDistributionAmountOfPartnerToken,
          ...rest
        }) => ({
          ...rest,
          borrowApy: { ...borrowApy, [asset.symbol]: assetBorrowApy },
          borrowDailyDistributionAmount: {
            ...borrowDailyDistributionAmount,
            [asset.symbol]: assetBorrowDailyDistributionAmount,
          },
          borrowDailyDistributionAmountOfPartnerToken: {
            ...borrowDailyDistributionAmountOfPartnerToken,
            [asset.symbol]: nonTonicBorrowDailyDistributeAmount,
          },
        })
      );
    }

    async function getAssetsBorrowApy(): Promise<void> {
      try {
        await Promise.all(
          supportedAssets.map((asset) => getAssetBorrowApy(asset))
        );
      } catch (error) {
        notify(error);
      }
    }

    async function getAssetsUsdPrice(): Promise<void> {
      try {
        supportedAssets.forEach((asset) =>
          dispatch(getAssetUsdPrice(tectonicAssetPricer, asset))
        );
      } catch (error) {
        notify(error);
      }
    }

    async function getData(): Promise<void> {
      getAssetsBorrowAmount();
      getAssetsBorrowApy();
      getAssetsSupplyAmount();
      getAssetsSupplyApy();
      getAssetsUsdPrice();
    }

    getData();
  }, [dispatch, sdk, tectonicAssetPricer, supportedAssets, mode]);

  const updateNetBorrowApy = (symbol: string, netApy: number) => {
    if (data.netBorrowApy[symbol] === netApy) return;

    setData((prev) => ({
      ...prev,
      netBorrowApy: { ...prev.netBorrowApy, [symbol]: netApy },
    }));
  };

  const updateNetSupplyApy = (symbol: string, netApy: number) => {
    if (data.netSupplyApy[symbol] === netApy) return;

    setData((prev) => ({
      ...prev,
      netSupplyApy: { ...prev.netSupplyApy, [symbol]: netApy },
    }));
  };

  return {
    data,
    updateNetBorrowApy,
    updateNetSupplyApy,
  };
}

export default useMarketsPageData;
